import React, { useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { IBotData } from "../../shared/types";
import { BotStatCard } from "../IndividualBotView/SingleBotCard";
import { AggregateStatsCard, AttentionItem } from "./AggregateSummaryCard";
import { combineBotsData } from "../../features/aggregateBotData";
import { processAggregatedHistory } from "../../features/enrichHistory";
import { SummaryBotCard } from "./SummaryBotCard";
import { useAuth } from "../../hooks/useAuth";
import { useDemoBotSelection } from "../../hooks/useDemoBotSelection";
import { processBuybackData } from "../../features/processBuybackData";
import BuybacksChart from "../../components/charts/BuybacksChart";
import { LowGasAlert } from "../../components/LowGasAlert";
import { WaitingFundsAlert } from "../../components/WaitingFundsAlert";
import { OutOfRangeAlert } from "../../components/OutOfRangeAlert";
import { isWaitingForFunds } from "../../utils/botUtils";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { poolIndexTrend, processPortfolioHistory } from "../../features/overviewSeries";
import { botState, gasSeverity, isInRange, worstStateTone } from "../../utils/botMetrics";
import { revealElement } from "../../utils/revealElement";
import { tokens } from "../../theme";

interface BotGroupContainerProps {
  bots: IBotData[];
  isHomeView: boolean;
  onDoubleClick?: (botId: string) => void;
  onDoubleClickSummaryBack?: () => void;
  /** Opens a bot's detail tab from its card (family view). */
  onSelectBot?: (botId: string) => void;
  activeNetwork: string;
}

export const BotGroupContainer: React.FC<BotGroupContainerProps> = ({
  bots,
  isHomeView,
  onDoubleClick,
  onDoubleClickSummaryBack,
  onSelectBot,
  activeNetwork,
}) => {
  const { groups } = useAuth();
  const isDemo = groups?.includes("DEMO");
  const { selectedBots: demoSelectedBots, openBotSelection } = useDemoBotSelection();
  const [, setSearchParams] = useSearchParams();

  const botsForAggregateSummary = useMemo(() => {
    return bots; // Always use all bots for aggregate summary
  }, [bots]);

  const botsForIndividualCards = useMemo(() => {
    if (isHomeView) {
      if (isDemo) {
        return bots.filter((bot) =>
          demoSelectedBots.includes(bot.stats.botName)
        );
      }
      if (activeNetwork === "ALL") return bots;
      return bots.filter((bot) =>
        bot.stats.botName.startsWith(`${activeNetwork}_`)
      );
    }
    return bots; // For individual bot view, use the bots passed (already filtered)
  }, [isHomeView, activeNetwork, bots, isDemo, demoSelectedBots]);

  const familyEntries = useMemo(
    () =>
      Object.entries(
        botsForIndividualCards.reduce((acc: Record<string, IBotData[]>, bot) => {
          const family = bot.stats.botName.replace(/_\d+$/, "");
          acc[family] = acc[family] || [];
          acc[family].push(bot);
          return acc;
        }, {})
      ).sort(([familyA], [familyB]) => familyA.localeCompare(familyB)),
    [botsForIndividualCards]
  );

  const familyTrends = useMemo(() => {
    if (!isHomeView) return {} as Record<string, number[]>;
    return Object.fromEntries(
      familyEntries.map(([family, familyBots]) => [family, poolIndexTrend(processPortfolioHistory(familyBots))])
    ) as Record<string, number[]>;
  }, [familyEntries, isHomeView]);

  const allHistoryBots = botsForIndividualCards.filter(
    (b) => b.history && b.history.length > 0
  );

  const performanceData = useMemo(
    () => processAggregatedHistory(botsForIndividualCards),
    [botsForIndividualCards]
  );

  // Same since-inception index everywhere (overview, family, bot), so the hero chip, the chart
  // and every card agree on LP vs HODL.
  const heroPerformance = useMemo(
    () => processPortfolioHistory(botsForAggregateSummary),
    [botsForAggregateSummary]
  );

  const buybackData = useMemo(
    () => processBuybackData(botsForIndividualCards, performanceData),
    [botsForIndividualCards, performanceData]
  );

  // Check if user is admin
  const isAdmin = groups?.includes("admin");

  // Bots earning no fees: position out of range, or no position (and not balancing). Critical, so their
  // alerts lead the list; out of range first, then by name (the order the attention chips jump in).
  const rangeAlertBots = useMemo(() => {
    const byName = (a: IBotData, b: IBotData) =>
      a.stats.botName.localeCompare(b.stats.botName, undefined, { numeric: true });
    const inState = (state: "out-of-range" | "idle") =>
      botsForIndividualCards.filter((bot) => botState(bot.stats) === state).sort(byName);
    return [...inState("out-of-range"), ...inState("idle")];
  }, [botsForIndividualCards]);

  // Detect bots with low gas
  const lowGasBots = useMemo(() => {
    return botsForIndividualCards.filter(bot => bot.stats.isLowGas === true);
  }, [botsForIndividualCards]);

  // Detect bots waiting for external funds
  const waitingFundsBots = useMemo(() => {
    return botsForIndividualCards.filter(bot => isWaitingForFunds(bot.stats));
  }, [botsForIndividualCards]);

  const sortedBots = [...botsForIndividualCards].sort((a, b) => {
    const aClosed = !a.stats.isPositionOpen;
    const bClosed = !b.stats.isPositionOpen;

    // First, prioritize open positions
    if (aClosed !== bClosed) {
      return aClosed ? 1 : -1; // closed goes last
    }

    // If both are open, sort by roleIndex
    if (!aClosed && !bClosed) {
      if (a.stats.roleIndex !== b.stats.roleIndex) {
        return a.stats.roleIndex - b.stats.roleIndex;
      }
      // If same roleIndex, sort by name
      return a.stats.botName.localeCompare(b.stats.botName);
    }

    // If both are closed, treat roleIndex as 999
    return a.stats.botName.localeCompare(b.stats.botName);
  });

  const hasAlerts = isAdmin && (rangeAlertBots.length > 0 || lowGasBots.length > 0 || waitingFundsBots.length > 0);

  // Opens a bot's tab from anywhere (overview included): family + tab in the URL, one history entry.
  const openBot = React.useCallback(
    (botId: string) => {
      if (!isHomeView && onSelectBot) {
        onSelectBot(botId);
        return;
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("f", botId.replace(/_\d+$/, ""));
        next.set("t", botId);
        next.delete("s");
        return next;
      });
      window.scrollTo({ top: 0 });
    },
    [isHomeView, onSelectBot, setSearchParams]
  );

  // "What needs attention" at a glance (admins), most severe first. A range / position chip opens the bot
  // when there is one, otherwise it jumps to the first of their alerts; gas and funds chips jump to theirs.
  const attention = useMemo<AttentionItem[]>(() => {
    if (!isAdmin) return [];
    const byName = (a: IBotData, b: IBotData) =>
      a.stats.botName.localeCompare(b.stats.botName, undefined, { numeric: true });
    const cardId = (botId: string) => (isHomeView ? `strategy-${botId.replace(/_\d+$/, "")}` : `bot-${botId}`);
    const goToBots = (ids: string[]) => () => {
      if (ids.length === 1) openBot(ids[0]);
      // The card is the fallback if the alert isn't on screen.
      else if (!revealElement(`alert-range-${ids[0]}`)) revealElement(cardId(ids[0]));
    };
    const goToAlert = (prefix: string, ids: string[]) => () => {
      revealElement(`${prefix}-${ids[0]}`);
    };
    const ids = (list: IBotData[]) => [...list].sort(byName).map((b) => b.stats.botName);
    const outOfRange = ids(rangeAlertBots.filter((b) => botState(b.stats) === "out-of-range"));
    const idle = ids(rangeAlertBots.filter((b) => botState(b.stats) === "idle"));
    const gas = ids(lowGasBots);
    const funds = ids(waitingFundsBots);
    const gasCritical = lowGasBots.some((b) => gasSeverity(b.stats) === "error");
    const items: AttentionItem[] = [
      { key: "range", label: "Out of range", count: outOfRange.length, tone: "error", onSelect: goToBots(outOfRange) },
      { key: "idle", label: "No position", count: idle.length, tone: "error", onSelect: goToBots(idle) },
      { key: "gas", label: "Low gas", count: gas.length, tone: gasCritical ? "error" : "warning", onSelect: goToAlert("alert-gas", gas) },
      { key: "funds", label: "Awaiting funds", count: funds.length, tone: "warning", onSelect: goToAlert("alert-funds", funds) },
    ];
    const rank = { error: 0, warning: 1 } as const;
    return items.filter((a) => a.count > 0).sort((a, b) => rank[a.tone] - rank[b.tone]);
  }, [isAdmin, isHomeView, rangeAlertBots, lowGasBots, waitingFundsBots, openBot]);

  // Land a revealed alert below the docked tab band and the 32px fade under it, so its top edge (and the
  // reveal ring) is fully visible: the band ends 72px down on desktop / iPad, ~132px on phones with the
  // top bar shown.
  const alertAnchorSx = { scrollMarginTop: { xs: 168, md: 108 }, borderRadius: { xs: "24px", md: "28px" } };

  return (
    <Box>
      <AggregateStatsCard
        bots={botsForAggregateSummary}
        isHomeView={isHomeView}
        performanceData={heroPerformance}
        attention={attention}
      />

      {hasAlerts && (
        <Box
          id="attention-alerts"
          sx={{
            scrollMarginTop: { xs: 72, md: 80 },
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: { xs: 1.5, md: 1.5 },
            mt: { xs: 1.5, md: 2 },
          }}
        >
          {/* Out of range / no position (critical) first - Admin only */}
          {rangeAlertBots.map((bot) => (
            <Box key={`range-${bot.stats.botName}`} id={`alert-range-${bot.stats.botName}`} sx={alertAnchorSx}>
              <OutOfRangeAlert stats={bot.stats} />
            </Box>
          ))}

          {/* Low Gas Alerts for bots in this view - Admin only */}
          {lowGasBots.map(bot => (
            <Box key={bot.stats.botName} id={`alert-gas-${bot.stats.botName}`} sx={alertAnchorSx}>
              <LowGasAlert
                stats={bot.stats}
                isAdmin={isAdmin}
                onCopy={(text) => {
                  navigator.clipboard.writeText(text);
                }}
              />
            </Box>
          ))}

          {/* Waiting for External Funds Alerts - Admin only */}
          {waitingFundsBots.map(bot => (
            <Box key={`waiting-${bot.stats.botName}`} id={`alert-funds-${bot.stats.botName}`} sx={alertAnchorSx}>
            <WaitingFundsAlert
              botName={bot.stats.botName}
              stepDescription={bot.stats.currentBalancingStep}
              isAdmin={isAdmin}
              amount={bot.stats.waitingFundsAmount}
              token={bot.stats.waitingFundsToken}
              chainId={bot.stats.waitingFundsChainId}
              walletAddress={bot.stats.waitingFundsAddress}
              binanceSubaccount={bot.stats.binanceDepositSubaccount}
              binanceToken={bot.stats.binanceDepositToken}
              binanceAmount={bot.stats.binanceDepositAmount}
              onCopy={(text) => {
                navigator.clipboard.writeText(text);
              }}
            />
            </Box>
          ))}
        </Box>
      )}

      <Box id="strategies" sx={{ mt: { xs: 4, md: 5 }, scrollMarginTop: { xs: 72, md: 80 } }}>
        <SectionHeader
          variant="page"
          title={isHomeView ? "Strategies" : "Bots"}
          subtitle={isHomeView ? `${familyEntries.length} strategies` : "Open a bot for the full detail"}
        />
        <Box
          sx={{
            display: "grid",
            gap: { xs: 1.5, md: 2 },
            gridTemplateColumns: isHomeView
              ? // auto-fit: cards share the row instead of leaving empty column tracks on wide screens.
                "repeat(auto-fit, minmax(min(100%, 300px), 1fr))"
              : "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
            alignItems: "stretch",
          }}
        >
          {isHomeView && familyEntries.length === 0 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                p: { xs: 3, md: 4 },
                textAlign: "center",
                borderRadius: "20px",
                border: `1px dashed ${tokens.borderStrong}`,
              }}
            >
              <Typography sx={{ fontWeight: 650, mb: 0.5 }}>No strategies selected</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose the strategies you want to follow; the fund totals above stay complete.
              </Typography>
              {isDemo && (
                <Button variant="contained" color="secondary" onClick={openBotSelection}>
                  Choose strategies
                </Button>
              )}
            </Box>
          )}
          {isHomeView
            ? familyEntries.map(([familyName, familyBots]) => (
                <SummaryBotCard
                  key={familyName}
                  id={`strategy-${familyName}`}
                  bot={combineBotsData(structuredClone(familyBots))}
                  inRangeCount={familyBots.filter((b) => isInRange(b.stats)).length}
                  outOfRangeCount={familyBots.filter((b) => botState(b.stats) === "out-of-range").length}
                  totalBotsCount={familyBots.length}
                  ringTone={worstStateTone(familyBots.map((b) => b.stats))}
                  awaitingFunds={familyBots.some((b) => isWaitingForFunds(b.stats))}
                  trend={familyTrends[familyName]}
                  onDoubleClick={onDoubleClick}
                />
              ))
            : sortedBots.map((bot) => (
                <BotStatCard key={bot.stats.botName} id={`bot-${bot.stats.botName}`} bot={bot} onSelect={onSelectBot} />
              ))}
        </Box>
      </Box>

      {allHistoryBots.length > 0 &&
        !isHomeView &&
        buybackData.length > 0 &&
        activeNetwork !== "RSK" &&
        activeNetwork !== "ARB" && (
          <Box sx={{ mt: { xs: 4, md: 5 } }}>
            <BuybacksChart
              data={buybackData}
              title="Treasury growth"
              subtitle={`Cumulative ${botsForIndividualCards[0]?.stats.repurchaseToken || "tokens"} repurchased`}
            />
          </Box>
        )}
    </Box>
  );
};
